import { Layout } from 'antd'
import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import Sidebar from './Sidebar'
import TopBar from './TopBar'

import { useMe } from '@/features/auth/hooks/useMe'
import { filterNavigationByPermissions } from '@/features/rbac/filterNavigation'
import { TRANSITIONS } from '@/shared/constants/transitions'
import { LayoutProvider, useLayoutContext } from '@/shared/contexts/LayoutContext'
import { useBreadcrumb } from '@/shared/hooks/useBreadcrumb'
import { useNotifications } from '@/shared/hooks/useNotifications'
import { useThemeColors } from '@/shared/hooks/useThemeColors'
import { useUserProfile } from '@/shared/hooks/useUserProfile'
import {
  selectIsSuperAdmin,
  selectPermissions,
} from '@/store/slices/authSlice'
import { MainLayoutProps } from '@/types/layout'
import { navigationConfig } from '@/types/navigation'



const { Content } = Layout

const MainLayoutContent: React.FC<MainLayoutProps> = ({ children, className = '' }) => {
  const {
    sidebarCollapsed,
    mobileMenuOpen,
    currentRoute,
    toggleSidebar,
    toggleMobileMenu,
    setMobileMenuOpen,
  } = useLayoutContext()

  const breadcrumbs = useBreadcrumb()
  const user = useUserProfile()
  const notifications = useNotifications()
  const colors = useThemeColors()
  const permissions = useSelector(selectPermissions)
  const isSuperAdmin = useSelector(selectIsSuperAdmin)
  // Live-sync permissions + profile with the BE. Dispatches into Redux on
  // every successful poll, so `permissions` / `isSuperAdmin` above stay fresh
  // without any extra plumbing. Mounting here (inside the authenticated
  // layout) guarantees we never poll on /login or /403.
  useMe()
  const filteredNavigation = useMemo(
    () => filterNavigationByPermissions(navigationConfig.items, permissions, isSuperAdmin),
    [permissions, isSuperAdmin],
  )
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Get page title from breadcrumbs
  const pageTitle = breadcrumbs.length > 0 
    ? breadcrumbs[breadcrumbs.length - 1].label 
    : ''

  return (
    <Layout 
      className={className}
      style={{ 
        minHeight: '100vh',
        backgroundColor: colors.isDark ? '#000' : '#F5F7F8',
        gap: '24px',
        transition: TRANSITIONS.BACKGROUND,
      }}
    >
      <Sidebar
        navigationItems={filteredNavigation}
        currentPath={currentRoute}
        collapsed={sidebarCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        onToggleCollapse={toggleSidebar}
        onCloseMobileMenu={() => setMobileMenuOpen(false)}
      />
      
      <Layout style={{ 
        backgroundColor: 'transparent',
        transition: TRANSITIONS.BACKGROUND,
        padding: isMobile ? '0' : '24px 24px 24px 0',
      }}>
        <TopBar
          breadcrumbs={breadcrumbs}
          pageTitle={pageTitle}
          user={user}
          notifications={notifications}
          sidebarCollapsed={sidebarCollapsed}
          onToggleMobileMenu={toggleMobileMenu}
        />
        
        <Content 
          className="custom-scrollbar" 
          role="main"
          id="main-content"
          style={{
            backgroundColor: colors.isDark ? '#0f2929' : '#FFFFFF',
            padding: '24px',
            minHeight: isMobile ? 'calc(100vh - 96px)' : 'calc(100vh - 144px)',
            overflow: 'auto',
            transition: TRANSITIONS.BACKGROUND,
            borderRadius: isMobile ? '0' : '16px',
            marginTop: isMobile ? '0' : '24px',
            boxShadow: isMobile ? 'none' : colors.isDark 
              ? '0 4px 24px rgba(0, 0, 0, 0.4)'
              : '0 4px 24px rgba(0, 0, 0, 0.08)',
          }}
        >
          {children}
        </Content>
      </Layout>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${colors.scrollThumb};
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${colors.scrollThumbHover};
        }
      `}</style>
    </Layout>
  )
}

const MainLayout: React.FC<MainLayoutProps> = (props) => {
  return (
    <LayoutProvider>
      <MainLayoutContent {...props} />
    </LayoutProvider>
  )
}

export default MainLayout
